import { ShieldCheck, Crown, Star, Lock, AlertTriangle, MessageCircle } from 'lucide-react';
import LegalLayout from './LegalLayout';
import { TrustSafetyES } from './LegalSpanish';
import { useLanguage } from '../../i18n/LanguageContext';

const Pillar = ({ Icon, color, title, body }) => (
  <div className="bg-surface rounded-2xl p-6 border border-border flex flex-col">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="font-heading text-lg font-bold text-text-primary mb-2">{title}</h3>
    <p className="text-sm text-text-secondary leading-relaxed">{body}</p>
  </div>
);

const TrustSafety = ({ user, setUser }) => {
  const { lang } = useLanguage();
  return (
    <LegalLayout
      user={user}
      setUser={setUser}
      titleKey="footer.linkTrust"
      titleFallback="Trust & Safety"
      esTitle="Confianza y Seguridad"
      subtitleFallback="Every cleaner on Match My Cleaner is vetted. Here's exactly how."
      esSubtitle="Cada limpiador en Match My Cleaner está verificado. Aquí está exactamente cómo."
      testId="trust-safety-page"
    >
      {lang === 'es' ? <TrustSafetyES /> : (
        <>
    <p>
      We built Match My Cleaner because letting a stranger into your home should never feel like a leap of faith. Every
      cleaner you see on this platform has cleared a multi-step verification process — and the badges on their profile
      tell you exactly how far they've gone.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose my-8">
      <Pillar
        Icon={ShieldCheck}
        color="bg-emerald-500"
        title="Background-checked"
        body="Every cleaner passes an industry-standard background check via Checkr — covering national criminal records, sex offender registries, and global watchlists. We don't show un-checked cleaners in search."
      />
      <Pillar
        Icon={Crown}
        color="bg-amber-500"
        title="Premium / Elite tiers"
        body="Cleaners earn Premium status with a 4.7+ average across 10+ reviews, and Elite at 4.9+ across 25+. These are reputation-based, not pay-to-play."
      />
      <Pillar
        Icon={Star}
        color="bg-rose-500"
        title="Verified reviews"
        body="Only customers who completed a paid booking can leave a review — no anonymous trolling, no incentivized 5-stars from friends."
      />
      <Pillar
        Icon={Lock}
        color="bg-text-primary"
        title="Secure payments"
        body="All payments flow through Stripe. We never store your card details, and your cleaner is never paid until you've confirmed the job."
      />
    </div>

    <h2>What "Background-checked" actually means</h2>
    <p>
      When a cleaner signs up, they consent to a background check run by <strong>Checkr</strong>, the same vetting
      provider used by Uber, Instacart, and Lyft. The check covers:
    </p>
    <ul>
      <li>SSN trace and identity verification</li>
      <li>National criminal records search (felonies and misdemeanors, 7 years)</li>
      <li>Sex offender registry across all 50 states</li>
      <li>Global watchlist (OFAC, FBI, Interpol)</li>
    </ul>
    <p>
      Only cleaners with a <strong>Clear</strong> report can appear in search results or accept bookings.
      Cleaners flagged as <em>Consider</em> are paused pending human review and a formal Adverse Action process —
      we follow FCRA compliance to the letter.
    </p>

    <h2>The cleaner agreement</h2>
    <p>
      Every cleaner signs a binding Platform Agreement at signup, committing to:
    </p>
    <ul>
      <li>Never solicit payment outside Match My Cleaner</li>
      <li>Never share personal contact information to circumvent the platform</li>
      <li>Show up on time, in clean uniform, with their own supplies (unless otherwise agreed)</li>
      <li>Honor cancellations and rescheduling per the platform rules</li>
    </ul>
    <p>
      Violation can result in suspension, forfeiture of pending earnings, and — in cases of platform bypass —
      pursuit of liquidated damages.
    </p>

    <h2>If something goes wrong</h2>
    <p>
      We take incidents seriously. If your cleaner doesn't show up, damages something, or behaves inappropriately:
    </p>
    <ul>
      <li><strong>Within 24 hours of the service</strong>, report it via Contact Us — we'll open an investigation immediately.</li>
      <li><strong>Refunds</strong> are processed back to your original card within 5-10 business days for confirmed issues.</li>
      <li><strong>Damage claims</strong> are reviewed case-by-case. Most cleaners carry their own liability insurance, which we'll help you coordinate with.</li>
      <li><strong>Safety incidents</strong> are reported to local authorities where appropriate. The cleaner is immediately suspended from the platform during investigation.</li>
    </ul>

    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 not-prose my-8 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="font-heading font-semibold text-amber-900 mb-1">Found something off?</h3>
        <p className="text-sm text-amber-900/80">
          See a profile that doesn't look right, or had a bad experience you want to flag privately?
          <a href="/contact" className="underline ml-1 font-semibold inline-flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" /> Reach out to our trust team
          </a>.
        </p>
      </div>
    </div>

    <p className="text-sm italic">
      Trust isn't a marketing claim — it's the floor on which every booking stands. If we ever drop below it, tell us.
    </p>
        </>
      )}
    </LegalLayout>
  );
};

export default TrustSafety;
